import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/redux/hooks";
import { useToggle } from "@/hooks/useToggle";

import {
  CopyPlus,
  User,
  FileCheck,
  Contact,
  ListCheck,
  Loader2,
  MapPin,
  Handshake,
} from "lucide-react";

import { NeedHelpMessage } from "@/components/pages/dashboard/need-help";
import Dashboard from "@/components/pages/dashboard";
import { Button } from "@/components/ui/button";
import { OpportunityRow } from "@/components/opportunity";
import { SearchingSection } from "@/components/pages/dashboard/searching";
import CandidateProfileVideoDialog from "@/components/dialogs/candidate/videos";
import CandidateFractionalExperienceEditFormDialog from "@/components/forms/candidate/update-fractional-experience";

import { useCandidateResourcesQuery } from "@/queries/candidate/resources";
import { useMyCandidateQuery } from "@/queries/candidate/my-candidate";
import { useProfileQuery } from "@/queries/user/profile";
import {
  Opportunity,
  useCandidateOpportunities,
} from "@/queries/candidate/opportunities";
import { useCompleteTask } from "@/mutations/candidate/dashboard/todo";
import OpportunityApplyDialog from "@/components/forms/candidate/opportunity/apply";
import { OpportunityDetailsDialog } from "@/components/dialogs/opportunity/details";

const CandidateDashboard = () => {
  const { profile, profileLoading } = useProfileQuery();
  const { candidate, candidateLoading } = useMyCandidateQuery();
  const { candidateOpportunities } = useCandidateOpportunities();

  const [showOpportunities, setShowOpportunities] = useState<boolean>(false);
  const [activeOpportunity, setActiveOpportunity] =
    useState<Opportunity | null>(null);
  const [activeOpportunityDetails, setActiveOpportunityDetails] =
    useState<Opportunity | null>(null);

  const handleApplyClick = (opportunity: Opportunity) => {
    setActiveOpportunity(opportunity);
  };

  const handleDetailsClick = (opportunity: Opportunity) => {
    setActiveOpportunityDetails(opportunity);
  };

  useEffect(() => {
    if (activeOpportunity) {
      const newOpportunity = candidateOpportunities?.find(
        (opportunity) => opportunity.id === activeOpportunity.id,
      );
      if (newOpportunity) {
        setActiveOpportunity(newOpportunity);
      }
    }

    if (activeOpportunityDetails) {
      const newOpportunity = candidateOpportunities?.find(
        (opportunity) => opportunity.id === activeOpportunityDetails.id,
      );
      if (newOpportunity) {
        setActiveOpportunityDetails(newOpportunity);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateOpportunities]);

  useMemo(() => {
    if (candidateOpportunities) {
      setShowOpportunities(
        candidateOpportunities.filter(
          ({ status, candidateStatus }) =>
            status !== "Role Filled (lost)" &&
            status !== "Role Filled (won)" &&
            candidateStatus &&
            candidateStatus.status !== "Not Interested" &&
            candidateStatus.status !== "Rejected for Role",
        ).length > 0,
      );
    }
  }, [candidateOpportunities]);

  return (
    <Dashboard.Root>
      <Dashboard.ProfileRow>
        <Dashboard.ProfileInfo
          title={profile?.name}
          description={
            candidate ? (
              <span className="flex flex-row gap-4 items-center mt-2">
                <span className="flex flex-row items-center gap-2">
                  <User className="size-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {candidate.titleAndEmployer}
                  </span>
                </span>
                <span className="flex flex-row items-center gap-2">
                  <MapPin className="size-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {candidate.location}
                  </span>
                </span>
              </span>
            ) : (
              `Loading...`
            )
          }
          fallbackIcon={User}
          src={profile?.profilePictureLink}
          loading={profileLoading || candidateLoading}
        />
      </Dashboard.ProfileRow>
      <Dashboard.MainContent>
        {showOpportunities ? (
          <MatchesSection
            handleApplyClick={handleApplyClick}
            handleDetailsClick={handleDetailsClick}
          />
        ) : candidate?.applicationComplete ? (
          <SearchingSection />
        ) : null}
        {activeOpportunity ? (
          <OpportunityApplyDialog
            opening={activeOpportunity}
            open={true}
            setOpen={(open: boolean) => !open && setActiveOpportunity(null)}
          />
        ) : null}
        {activeOpportunityDetails ? (
          <OpportunityDetailsDialog
            opening={activeOpportunityDetails}
            open={true}
            setOpen={(open: boolean) =>
              !open && setActiveOpportunityDetails(null)
            }
          />
        ) : null}
        <ApprovalSection />
        <NeedHelpMessage />
        <SelectedResourcesSection />
      </Dashboard.MainContent>
    </Dashboard.Root>
  );
};

export default CandidateDashboard;

const MatchesSection = ({
  handleApplyClick,
  handleDetailsClick,
}: {
  handleApplyClick: (o: Opportunity) => void;
  handleDetailsClick: (o: Opportunity) => void;
}) => {
  const { candidateOpportunities, candidateOpportunitiesLoading } =
    useCandidateOpportunities();

  const navigate = useNavigate();

  const [openOpportunities, setOpenOpportunities] = useState<Opportunity[]>([]);

  useMemo(() => {
    if (candidateOpportunities !== null) {
      setOpenOpportunities(
        candidateOpportunities.filter(
          ({ status, candidateStatus }) =>
            status !== "Role Filled (lost)" &&
            status !== "Role Filled (won)" &&
            candidateStatus &&
            candidateStatus.status !== "Not Interested" &&
            candidateStatus.status !== "Rejected for Role",
        ),
      );
    }
  }, [candidateOpportunities]);

  return (
    <Dashboard.Collapsible defaultOpen>
      <Dashboard.CollapsibleHeader>
        <Dashboard.CollapsibleTitle className="flex flex-row items-center gap-2">
          Your Matches
          {candidateOpportunitiesLoading ? (
            <Loader2 className="animate-spin size-4" />
          ) : (
            <p className="w-fit text-xs bg-border/50 border rounded-full px-2 py-1">
              {openOpportunities.length} new
            </p>
          )}
        </Dashboard.CollapsibleTitle>
      </Dashboard.CollapsibleHeader>
      <Dashboard.CollapsibleContent>
        <div className="w-full flex flex-col rounded-lg border">
          {openOpportunities.map((opportunity) => (
            <OpportunityRow
              key={opportunity.id}
              opportunity={opportunity}
              handleApplyClick={handleApplyClick}
              handleDetailsClick={handleDetailsClick}
            />
          ))}
        </div>
        <div className="flex flex-row gap-4 mt-4">
          <Button
            variant="secondary"
            onClick={() => {
              navigate("/opportunities");
            }}
          >
            <Handshake />
            View all opportunities
          </Button>
        </div>
      </Dashboard.CollapsibleContent>
    </Dashboard.Collapsible>
  );
};

const ApprovalSection = () => {
  const navigate = useNavigate();

  const user = useAppSelector((state) => state.auth.user);
  const { candidate, candidateLoading } = useMyCandidateQuery();
  const { completeProfile, completeProfileVideo, completeTaskPending } =
    useCompleteTask();

  const [progress, setProgress] = useState<number>(0);
  const [videoDialogOpen, toggleVideoDialogOpen] = useToggle();

  useMemo(() => {
    let tempProgress = 0;
    if (candidate) {
      if (candidate.applicationReviewed) tempProgress += 30;
      if (candidate.interviewComplete) tempProgress += 30;
      if (candidate.cultureComplete) tempProgress += 25;
      if (candidate.profileComplete) tempProgress += 5;
      if (candidate.profileVideoComplete) tempProgress += 5;
      if (candidate.fractionalExperience !== undefined) tempProgress += 5;
    }

    setProgress(tempProgress);
  }, [candidate]);

  if (progress === 100) {
    return;
  }

  return (
    <Dashboard.Collapsible defaultOpen={progress !== 100 ? true : false}>
      <Dashboard.CollapsibleHeader>
        <div>
          <Dashboard.CollapsibleTitle>Your Approval</Dashboard.CollapsibleTitle>
          <p className="w-fit text-xs bg-border/50 border rounded-full px-2 py-1 group-hover:text-decoration-none">
            {progress === 100 ? "Complete" : "In Progress"}
          </p>
        </div>
        <Dashboard.CollapsibleProgress
          loading={candidateLoading}
          value={progress}
        />
      </Dashboard.CollapsibleHeader>
      <Dashboard.CollapsibleContent>
        <Dashboard.TodoContainer>
          <Dashboard.TodoItem
            icon={CopyPlus}
            title="Share your previous fractional experience"
            description="Help us understand the fractional work you've done"
            completed={candidate?.fractionalExperience !== undefined}
            loading={candidateLoading}
            secondary
          >
            {candidate?.applicationComplete &&
              candidate.fractionalExperience === undefined && (
                <Dashboard.TodoItemActions>
                  <CandidateFractionalExperienceEditFormDialog
                    profileId={user?.id ?? ""}
                    button={<Button variant="secondary">Get Started</Button>}
                    defaultOpen
                  />
                </Dashboard.TodoItemActions>
              )}
          </Dashboard.TodoItem>
          <Dashboard.TodoItem
            icon={FileCheck}
            title="Application approval"
            description="Your application goes through the review and approval process"
            completed={candidate?.applicationReviewed}
            loading={candidateLoading}
            secondary
          ></Dashboard.TodoItem>
          <Dashboard.TodoItem
            icon={Contact}
            title="Video candidate interview"
            description="Complete a brief video interview to share your skills, experience, and career goals"
            completed={candidate?.interviewComplete}
            loading={candidateLoading}
            secondary
          >
            {candidate?.applicationReviewed &&
              !candidate?.interviewComplete && (
                <Dashboard.TodoItemActions>
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/candidate-interview")}
                  >
                    Complete Video Interview
                  </Button>
                </Dashboard.TodoItemActions>
              )}
          </Dashboard.TodoItem>
          <Dashboard.TodoItem
            icon={ListCheck}
            title="Cultural fit evaluation"
            description="Take a quick assessment to see how your values and work style align"
            completed={candidate?.cultureComplete}
            loading={candidateLoading}
            secondary
          ></Dashboard.TodoItem>
          <Dashboard.TodoItem
            icon={CopyPlus}
            title="Add your video interview to your profile"
            description={
              candidate?.applicationComplete
                ? "Videos give hiring managers a sense of who you are"
                : "Once you have been approved, you will be able to add videos to your profile"
            }
            completed={candidate?.profileVideoComplete}
            loading={candidateLoading}
            secondary
          >
            {candidate?.applicationComplete &&
              !candidate?.profileVideoComplete && (
                <Dashboard.TodoItemActions>
                  <Button
                    variant="ghost"
                    disabled={completeTaskPending}
                    onClick={completeProfileVideo}
                  >
                    {completeTaskPending && (
                      <Loader2 className="animate-spin" />
                    )}
                    Mark as Complete
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => toggleVideoDialogOpen()}
                  >
                    Get Started
                  </Button>
                </Dashboard.TodoItemActions>
              )}
          </Dashboard.TodoItem>
          <Dashboard.TodoItem
            icon={CopyPlus}
            title="Complete your profile"
            description={
              candidate?.applicationComplete
                ? "Complete your profile with key details like your bio, experience, and references"
                : "Once you have been approved, you will be able to complete your profile"
            }
            completed={candidate?.profileComplete}
            loading={candidateLoading}
            secondary
          >
            {candidate?.applicationComplete && !candidate?.profileComplete && (
              <Dashboard.TodoItemActions>
                <Button
                  variant="ghost"
                  disabled={completeTaskPending}
                  onClick={completeProfile}
                >
                  {completeTaskPending && <Loader2 className="animate-spin" />}
                  Mark as Complete
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    navigate(user?.onboarded ? "/profile/edit" : "/onboarding");
                  }}
                >
                  Get Started
                </Button>
              </Dashboard.TodoItemActions>
            )}
          </Dashboard.TodoItem>
        </Dashboard.TodoContainer>
        <CandidateProfileVideoDialog
          userId={user?.id ?? ""}
          videos={candidate?.videos ?? []}
          open={videoDialogOpen}
          setOpen={toggleVideoDialogOpen}
          onSuccess={() => {
            completeProfileVideo();
            toggleVideoDialogOpen(false);
            navigate("/profile/edit");
          }}
        />
      </Dashboard.CollapsibleContent>
    </Dashboard.Collapsible>
  );
};

const SelectedResourcesSection = () => {
  const { candidateResources, candidateResourcesLoading } =
    useCandidateResourcesQuery();

  return (
    <Dashboard.ResourcesCollapsible
      resources={candidateResources ?? []}
      loading={candidateResourcesLoading}
    />
  );
};
